import { Routes, Route } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import DashboardLayout from '../components/layouts/DashboardLayout';
import RoleBasedRedirect from './RoleBasedRedirect';
import { CustomerProvider } from '../contexts/CustomerContext';
import { SearchProvider } from '../contexts/SearchContext';
import { useNavigation } from '../hooks/useNavigation';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { allPages } from './navigationConfig';
import CustomerRouteValidator from './CustomerRouteValidator';
import { useEffect } from 'react';
import { useCustomer } from '../contexts/CustomerContext';
import useSessionTimeout from '../hooks/useSessionTimeout';

// Clients

import Login from '../pages/Login';
import ZohoCallbackPage from '../pages/ZohoCallbackPage';
import SearchResults from '../pages/SearchResults';

// Not Found Page
import NotFoundPage from '../pages/NotFoundPage';

// Global debug logger that runs on ALL routes within the app
const GlobalDebugLogger = () => {
  const location = useLocation();
  const { customerId } = useParams();
  const { currentCustomer, isImpersonating } = useCustomer();

  useEffect(() => {
    // console.log('🌐 GLOBAL DEBUG - Current Route:', location.pathname);
    // console.log('🌐 GLOBAL DEBUG - customerId from URL:', customerId || 'NOT PRESENT');
    // console.log('🌐 GLOBAL DEBUG - currentCustomer:', currentCustomer);
    // console.log('🌐 GLOBAL DEBUG - isImpersonating:', isImpersonating);
    // console.log('🌐 GLOBAL DEBUG - Route has customerId param:', !!customerId);
    // console.log('---');
  }, [location.pathname, customerId, currentCustomer, isImpersonating]);

  return null;
};

const DebugRoutes = () => {
  const { routes } = useNavigation('client');
  const { customerId: urlCustomerId } = useParams();
  const { currentCustomer } = useCustomer();
  const { customerConfig, pageCategories } = require('./navigationConfig');

  // Default to 'helios' if customerId is not present in URL
  const customerId = urlCustomerId || 'helios';

  useEffect(() => {
    // console.log('🔍 DEBUG - CUSTOMER ID FROM URL:', customerId);
    // console.log('DEBUG - currentCustomer:', currentCustomer);
    // console.log('DEBUG - routes:', routes);
    // console.log('DEBUG - customerConfig for this ID:', customerConfig[customerId]);
    // console.log('DEBUG - default config:', customerConfig.default);
    
    // Check if the ID is different in some way (case sensitivity, etc.)
    if (customerId === 'helios') {
      // console.log('DEBUG - This is the helios client');
      // console.log('DEBUG - helios config exists:', !!customerConfig.helios);
      // console.log('DEBUG - helios config category:', customerConfig.helios?.category);
      // console.log('DEBUG - VOICE_AI category:', pageCategories.VOICE_AI);
      
      // Check if the routes include the calls page
      const callsRoute = routes.find(r => r.path === 'calls');
      // console.log('DEBUG - calls route exists:', !!callsRoute);
      
      if (!callsRoute) {
        // Check why it's not included
        const allCallsPage = require('./navigationConfig').allPages.callLogs;
        // console.log('DEBUG - allPages.callLogs:', allCallsPage);
        // console.log('DEBUG - callLogs category:', allCallsPage.category);
        // console.log('DEBUG - category match:', allCallsPage.category === customerConfig.helios?.category);
      }
    }
  }, [customerId, currentCustomer, routes]);

  return null;
};



const CustomerRoutes = () => {
  const { routes } = useNavigation('client');
  const { customerId: urlCustomerId } = useParams();

  // Default to 'helios' if customerId is not present in URL
  const customerId = urlCustomerId || 'helios';

  // console.log('🎯 CustomerRoutes - customerId:', customerId);

  // Redirect from base /app/:customerId to their specific home page.
  if (!routes.length) {
    // If no routes are available for this customer, show a 404.
    return <NotFoundPage />;
  }
  
  
  // Find the customer's home page.
  // First, look for a page with the specific pattern 'customerId-home'.
  const specificHomePage = routes.find(r => r.path === `${customerId}-home`);
  
  // If a specific home page exists, use its path. Otherwise, default to the
  // very first page in their navigation list. This removes the dependency on a generic 'home' path.
  const homePath = specificHomePage ? specificHomePage.path : routes[0]?.path;

  if (!homePath) {
    // This case should be rare, but covers customers with an empty but existing routes list.
    return <NotFoundPage />;
  }

  return (
    <Routes>
        <Route index element={<Navigate to={homePath} replace />} />
        {routes.map(route => (
            <Route key={route.path} path={route.path} element={route.element} />
        ))}
        <Route path="*" element={<NotFoundPage />} /> {/* Customer-specific 404 */}
    </Routes>
  );
};

const AppRoutes = () => {
  // Initialize session timeout management (60 minutes of inactivity)
  useSessionTimeout();

  return (
    <Routes>
      {/* Public Login Route */}
      <Route path="/" element={<Login />} />

      <Route path="/app/zoho/callback" element={
        <PrivateRoute>
          <ZohoCallbackPage />
        </PrivateRoute>
      } />

      {/* Protected Routes */}
      <Route
        path="/app"
        element={
          <PrivateRoute>
            <CustomerProvider>
              <SearchProvider>
                <GlobalDebugLogger />
                <DashboardLayout />
              </SearchProvider>
            </CustomerProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<RoleBasedRedirect />} />

        {/* Add Pages for routes as Design is being completed */}
        {/* Admin Pages */}
        <Route path="admin-dashboard" element={allPages.adminDashboard.element} />
        <Route path="admin-clients" element={allPages.adminClients.element} />
        <Route path="admin-activity" element={allPages.adminActivity.element} />
        <Route path="admin-advanced" element={allPages.adminAdvanced.element} />
        <Route path="customer-bootstrap" element={allPages.customerBootstrap.element} />
        <Route path="alerts" element={allPages.adminAlerts.element} />
        {/* Note Tickets is temporarily inside Admin category till it's completed */}
        <Route path="tickets" element={allPages.tickets.element} />
        <Route path="settings" element={allPages.settings.element} />
        <Route path="help" element={allPages.help.element} />
        {/* Note Chat is temporarily inside Admin category till it's completed */}
        <Route path="chat" element={allPages.chat.element} />
        <Route path="andy-notes" element={allPages.andyNotes.element} />
        <Route path="search" element={<SearchResults />} />
        <Route path="equipment-review" element={allPages.equipmentReview.element} />
        <Route path="policy-review" element={allPages.policyReview.element} />
        <Route path="token-metrics" element={allPages.tokenMetrics.element} />
        
        {/* <Route path="admin-insights" element={
          <PrivateRoute allowedRoles={['helios_admin']}>
            <AdminInsights />
          </PrivateRoute>
        } />
        <Route path="admin-alerts" element={
          <PrivateRoute allowedRoles={['helios_admin']}>
            <AdminAlerts />
          </PrivateRoute>
        } />
        */}



        <Route 
          path=":customerId/*" element={
            <CustomerRouteValidator>
              <DebugRoutes />
              <CustomerRoutes />
            </CustomerRouteValidator>
          } 
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
